import { useCallback, useEffect, useRef, useState } from 'react';

import { TAttachmentOnClient } from '@entities/attachment/model/types';

const REPLACE_DELAY_MS = 800;

interface IUseFileReplaceProps {
    droppedFiles: TAttachmentOnClient[];
    setDroppedFiles: (files: TAttachmentOnClient[]) => void;
}

type TTimer = ReturnType<typeof setTimeout>;

export const useFileReplace = ({ droppedFiles, setDroppedFiles }: IUseFileReplaceProps) => {
    const [fileReplaceLoader, setFileReplaceLoader] = useState(false);

    const replaceTimerRef = useRef<TTimer | null>(null);
    const nextFilesRef = useRef<TAttachmentOnClient[] | null>(null);

    const droppedFilesRef = useRef<TAttachmentOnClient[]>(droppedFiles);
    useEffect(() => {
        droppedFilesRef.current = droppedFiles;
    }, [droppedFiles]);

    const clearReplaceTimer = useCallback(() => {
        if (!replaceTimerRef.current) return;
        clearTimeout(replaceTimerRef.current);
        replaceTimerRef.current = null;
    }, []);

    const applyFiles = useCallback(
        (files: TAttachmentOnClient[]) => {
            setDroppedFiles(files);
        },
        [setDroppedFiles]
    );

    const clearDroppedFiles = useCallback(() => {
        clearReplaceTimer();
        nextFilesRef.current = null;
        setFileReplaceLoader(false);
        applyFiles([]);
    }, [applyFiles, clearReplaceTimer]);

    const setAttachmentsWithDelay = useCallback(
        (next: TAttachmentOnClient[]) => {
            const prev = droppedFilesRef.current;

            const hasPrev = prev.length > 0;
            const hasNext = next.length > 0;

            if (!hasPrev || !hasNext) {
                clearReplaceTimer();
                nextFilesRef.current = null;
                setFileReplaceLoader(false);
                applyFiles(next);
                return;
            }

            const prevId = prev[0]?.id;
            const nextId = next[0]?.id;

            if (prevId && nextId && prevId === nextId) {
                applyFiles(next);
                return;
            }

            nextFilesRef.current = next;

            // гарантируем, что таймер один
            clearReplaceTimer();
            setFileReplaceLoader(true);

            replaceTimerRef.current = setTimeout(() => {
                const toApply = nextFilesRef.current ?? next;
                nextFilesRef.current = null;
                setFileReplaceLoader(false);
                applyFiles(toApply);
                replaceTimerRef.current = null;
            }, REPLACE_DELAY_MS);
        },
        [applyFiles, clearReplaceTimer]
    );

    useEffect(
        () => () => {
            clearReplaceTimer();
            nextFilesRef.current = null;
        },
        [clearReplaceTimer]
    );

    return {
        fileReplaceLoader,
        setAttachmentsWithDelay,
        clearDroppedFiles,
    };
};

// import { useEffect, useRef, useState } from 'react';
//
// import { TAttachmentOnClient } from '@entities/attachment/model/types';
//
// const REPLACE_DELAY_MS = 800;
//
// interface IUseFileReplaceProps {
//     droppedFiles: TAttachmentOnClient[];
//     setDroppedFiles: (droppedFiles: TAttachmentOnClient[]) => void;
// }
//
// export const useFileReplace = ({ droppedFiles, setDroppedFiles }: IUseFileReplaceProps) => {
//     const [fileReplaceLoader, setFileReplaceLoader] = useState(false);
//
//     const replaceTimerRef = useRef<number | null>(null);
//     const nextFilesRef = useRef<TAttachmentOnClient[] | null>(null);
//
//     const clearReplaceTimer = () => {
//         if (replaceTimerRef.current) {
//             window.clearTimeout(replaceTimerRef.current);
//             replaceTimerRef.current = null;
//         }
//     };
//
//     const applyFiles = (files: TAttachmentOnClient[]) => {
//         setDroppedFiles(files);
//     };
//
//     const setAttachmentsWithDelay = (next: TAttachmentOnClient[]) => {
//         const hasPrev = droppedFiles.length > 0;
//         const hasNext = next.length > 0;
//
//         if (!hasPrev || !hasNext) {
//             clearReplaceTimer();
//             nextFilesRef.current = null;
//             setFileReplaceLoader(false);
//             applyFiles(next);
//             return;
//         }
//
//         const prevId = droppedFiles[0]?.id;
//         const nextId = next[0]?.id;
//
//         if (prevId && nextId && prevId === nextId) {
//             applyFiles(next);
//             return;
//         }
//
//         clearReplaceTimer();
//         nextFilesRef.current = next;
//         setFileReplaceLoader(true);
//
//         replaceTimerRef.current = window.setTimeout(() => {
//             applyFiles(nextFilesRef.current ?? next);
//             nextFilesRef.current = null;
//             setFileReplaceLoader(false);
//             replaceTimerRef.current = null;
//         }, REPLACE_DELAY_MS);
//     };
//
//     const clearDroppedFiles = () => {
//         clearReplaceTimer();
//         nextFilesRef.current = null;
//         setFileReplaceLoader(false);
//         setDroppedFiles([]);
//     };
//
//     useEffect(() => () => clearReplaceTimer(), []);
//
//     return {
//         fileReplaceLoader,
//         setAttachmentsWithDelay,
//         clearDroppedFiles,
//     };
// };
